import posthog from 'posthog-js';
import { AnalyticsAdapter, AnalyticsConfig, UserTraits, EventProperties, PageData } from '../types';

export class PostHogAdapter implements AnalyticsAdapter {
  name = 'posthog';
  private isInitialized = false;

  async initialize(config: AnalyticsConfig): Promise<void> {
    if (!config.enabled) return;

    const provider = config.providers.find(p => p.name === 'posthog');
    if (!provider?.key) {
      console.error('PostHog: no API key configured');
      return;
    }

    posthog.init(provider.key, {
      api_host: 'https://eu.i.posthog.com',
      person_profiles: 'identified_only',
      capture_pageview: false,
      autocapture: false,
      disable_session_recording: false,
      debug: config.debug,
    });

    this.isInitialized = true;
  }

  identify(userId: string, traits?: UserTraits): void {
    if (!this.isInitialized) return;
    posthog.identify(userId, traits);
  }

  track(eventName: string, properties?: EventProperties): void {
    if (!this.isInitialized) return;
    posthog.capture(eventName, properties);
  }

  page(data?: PageData): void {
    if (!this.isInitialized) return;
    posthog.capture('$pageview', {
      $current_url: data?.path ? window.location.origin + data.path : window.location.href,
      title: data?.title || document.title,
      search: data?.search || window.location.search,
    });
  }

  reset(): void {
    if (!this.isInitialized) return;
    posthog.reset();
  }

  loaded(): boolean {
    return this.isInitialized;
  }

  isFeatureEnabled(flag: string): boolean {
    if (!this.isInitialized) return false;
    return posthog.isFeatureEnabled(flag) ?? false;
  }

  getFeatureFlag(flag: string): string | boolean | undefined {
    if (!this.isInitialized) return undefined;
    return posthog.getFeatureFlag(flag) as string | boolean | undefined;
  }
}
